import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { MoreHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { AccountDetail } from "@/lib/types";
import { stageLabel, sourceLabel, STAGES } from "@/lib/format";
import { Button, Loading, Pill, Dot, Avatar, Menu, Modal, Field, toast } from "@/components/ui";
import { useMe } from "@/app/MeContext";
import { OverviewTab } from "./account/OverviewTab";
import { NotesTab } from "./account/NotesTab";
import { DocsTab } from "./account/DocsTab";
import { EmailsTab } from "./account/EmailsTab";
import { TicketsTab } from "./account/TicketsTab";
import { QuotesBillingTab } from "./account/QuotesBillingTab";
import { SystemsTab } from "./account/SystemsTab";
import { TerritoryBanner } from "./account/TerritoryWarning";
import { EditAccountModal } from "./account/EditAccountModal";
import { ComposeModal } from "./inbox/ComposeModal";
import { NewTicketModal } from "./tickets/NewTicketModal";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "emails", label: "Emails", count: "emails" },
  { key: "notes", label: "Notes", count: "notes" },
  { key: "tickets", label: "Tickets", count: "tickets" },
  { key: "docs", label: "Docs", count: "docs" },
  { key: "quotes", label: "Quotes and billing" },
  { key: "systems", label: "Systems", count: "systems" },
] as const;

export function AccountPage() {
  const { id = "", tab = "overview" } = useParams();
  const nav = useNavigate();
  const me = useMe();
  const { data: a, loading, refetch } = useQuery<AccountDetail>(`account:${id}`, () => api.get<AccountDetail>(`/accounts/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const [compose, setCompose] = useState(false);
  const [newTicket, setNewTicket] = useState(false);
  const [stageModal, setStageModal] = useState(false);

  if (loading && !a) return <Loading />;
  if (!a) return <div className="page"><div className="card"><div className="empty">Account not found.</div></div></div>;

  const red = a.systems.find((s) => s.status === "red");
  const yellow = a.systems.find((s) => s.status === "yellow");
  const live = a.systems.some((s) => s.live);
  const counts: Record<string, number> = { ...a.counts, systems: a.systems.length };
  const primary = a.contacts.find((c) => c.isPrimary);

  const archive = async () => {
    if (!confirm(`Archive ${a.name}? It will be hidden from lists.`)) return;
    await api.patch(`/accounts/${a.id}`, { archived: !a.archived });
    invalidate("accounts"); refetch();
    toast(a.archived ? "Account restored" : "Account archived");
  };

  return (
    <>
      <div className="breadcrumb desktop-only"><Link to="/accounts">Accounts</Link> <span aria-hidden="true">/</span> <span className="cur">{a.name}</span></div>
      <div className="mobile-header"><Link to="/accounts" className="muted-2">‹ Accounts</Link><span className="ml-auto"><Menu trigger={<Button variant="ghost" size="sm" className="icon"><MoreHorizontal size={16} /></Button>} items={[{ label: "Edit account", onClick: () => setEditing(true) }, { label: "Change stage", onClick: () => setStageModal(true) }, { label: a.archived ? "Restore" : "Archive", onClick: archive, danger: !a.archived }]} /></span></div>
      <header className="acct-header">
        <div className="row wrap gap-16" style={{ alignItems: "flex-start" }}>
          <Avatar name={a.name} large />
          <div className="col gap-6 acct-title">
            <div className="row wrap gap-10">
              <h1>{a.name}</h1>
              <span className="mono small muted">{a.number}</span>
            </div>
            <div className="row wrap gap-8">
              {red ? <Pill color="red"><Dot color="red" pulse />System down</Pill> : yellow ? <Pill color="yellow"><Dot color="yellow" />Change request</Pill> : live ? <Pill color="green"><Dot color="green" />Live</Pill> : null}
              <Pill color="bright" className="click" onClick={() => setStageModal(true)}>{stageLabel(a.stage)}</Pill>
              {a.businessType && <Pill>{a.businessType}</Pill>}
              <Pill>{a.partner ? `Partner: ${a.partner.name}` : `Source: ${sourceLabel(a.source)}`}</Pill>
              <span className="small muted">{primary ? `Primary: ${primary.name}` : "No primary contact"}{a.assignedTo.length ? ` · Assigned: ${a.assignedTo.map((u) => u.name).join(", ")}` : ""}</span>
            </div>
          </div>
          <div className="row gap-8 wrap desktop-only">
            <Button onClick={() => setCompose(true)}>Email</Button>
            <Button onClick={() => setNewTicket(true)}>New ticket</Button>
            {me.permissions.sendQuotes && <Button variant="primary" onClick={() => nav(`/quotes?new=${a.id}`)}>New quote</Button>}
            <Menu trigger={<Button className="icon" aria-label="More actions"><MoreHorizontal size={14} /></Button>} items={[{ label: "Edit account", onClick: () => setEditing(true) }, { label: "Change stage", onClick: () => setStageModal(true) }, ...(a.archived ? [{ label: "Restore account", onClick: archive }] : [{ label: "Archive account", onClick: archive, danger: true }])]} />
          </div>
        </div>
        <nav className="tabs" aria-label="Account sections">
          {TABS.map((t) => (
            <Link key={t.key} to={`/accounts/${a.id}${t.key === "overview" ? "" : "/" + t.key}`} className={`tab ${tab === t.key ? "active" : ""}`}>
              {t.label}{"count" in t && t.count && counts[t.count] > 0 ? <span className="count">{counts[t.count]}</span> : null}
            </Link>
          ))}
        </nav>
      </header>
      <div className="page" style={{ paddingTop: 24 }}>
        {a.territoryWarning && <div className="mb-16"><TerritoryBanner accountId={a.id} warning={a.territoryWarning} onDismissed={refetch} /></div>}
        {tab === "overview" && <OverviewTab account={a} refetch={refetch} onEmail={() => setCompose(true)} onTicket={() => setNewTicket(true)} />}
        {tab === "emails" && <EmailsTab scope={{ accountId: a.id }} onCompose={() => setCompose(true)} />}
        {tab === "notes" && <NotesTab scope={{ accountId: a.id }} />}
        {tab === "tickets" && <TicketsTab account={a} onNew={() => setNewTicket(true)} />}
        {tab === "docs" && <DocsTab scope={{ accountId: a.id }} contacts={a.contacts} refetch={refetch} />}
        {tab === "quotes" && <QuotesBillingTab account={a} />}
        {tab === "systems" && <SystemsTab account={a} refetch={refetch} />}
      </div>
      {editing && <EditAccountModal account={a} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refetch(); invalidate("accounts"); }} />}
      {compose && <ComposeModal accountId={a.id} to={primary?.email ?? a.contacts.find((c) => c.email)?.email ?? ""} onClose={() => setCompose(false)} />}
      {newTicket && <NewTicketModal accountId={a.id} systems={a.systems} onClose={() => setNewTicket(false)} onCreated={() => { setNewTicket(false); refetch(); nav(`/accounts/${a.id}/tickets`); }} />}
      {stageModal && <StageModal current={a.stage} onClose={() => setStageModal(false)} onPick={async (stage) => { await api.patch(`/accounts/${a.id}`, { stage }); setStageModal(false); refetch(); invalidate("accounts"); invalidate("pipeline"); }} />}
    </>
  );
}

function StageModal({ current, onClose, onPick }: { current: string; onClose: () => void; onPick: (s: string) => Promise<void> }) {
  const [stage, setStage] = useState(current);
  return (
    <Modal title="Change stage" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" onClick={() => onPick(stage)}>Save</Button></>}>
      <Field label="Stage"><select className="select" value={stage} onChange={(e) => setStage(e.target.value)}>{STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></Field>
    </Modal>
  );
}
